'use client'

import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useMemo, useState } from 'react'

import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { parseExchangeField, toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { isOrderEligibleAccount } from '@/lib/utils/account-select'
import { resolveAccount } from '@/lib/utils/resolve-account'
import {
  defaultOrderAccountByTradeVenueAtom,
  lastOrderAccountByTradeVenueAtom,
} from '@/state/order'
import { AccountBinding, SelectedInstrument, SelectedOption } from '@/types/dashboard'
import { PublicAccount } from '@/types/generated/common/account'
import { Exchange } from '@/types/generated/common/types'

interface UseOrderFormAccountResolutionArgs {
  /** Every account the user can see (`useUserAccounts().data.accounts`). */
  accounts: PublicAccount[] | undefined
  primaryExchange: Exchange
  /** Only the card's explicit/tab-pinned selection — see useUnifiedOrderForm. */
  cardTradingAccountId: string | undefined
  selectedInstruments: SelectedInstrument[]
  selectedOptions: SelectedOption[]
  setSelectedInstruments: (
    instruments: SelectedInstrument[] | ((prev: SelectedInstrument[]) => SelectedInstrument[])
  ) => void
  setSelectedOptions: (
    options: SelectedOption[] | ((prev: SelectedOption[]) => SelectedOption[])
  ) => void
}

/**
 * Which account each order-form row submits under. Owns the user's AccountTab
 * overrides and exposes the one resolver (`resolveItemAccountId`) that both item
 * normalization and submission go through, so "what the AccountTab shows" and
 * "what actually submits" can't diverge.
 */
export function useOrderFormAccountResolution({
  accounts,
  primaryExchange,
  cardTradingAccountId,
  selectedInstruments,
  selectedOptions,
  setSelectedInstruments,
  setSelectedOptions,
}: UseOrderFormAccountResolutionArgs) {
  const [lastAccountByTradeVenue, setLastAccountByTradeVenue] = useAtom(
    lastOrderAccountByTradeVenueAtom
  )
  const defaultAccountByTradeVenue = useAtomValue(defaultOrderAccountByTradeVenueAtom)
  // Exchange-level "which account did the user explicitly pick via the AccountTab
  // strip" (unified-order-form.tsx) — a LOCAL OVERRIDE only, never a computed
  // default. Every read goes through resolveInheritedAccountId/resolveAccount, which
  // re-derives the default live on every call; nothing here is ever written by an
  // effect, only by the user's own click in setOrderAccountFilter. Keyed by Exchange enum.
  const [orderAccountOverrides, setOrderAccountOverrides] = useState<Record<number, string>>({})

  // Keep every order-form account picker aligned with the accounts that can
  // actually submit orders. Read-only and non-active accounts remain visible
  // in account-management/viewing surfaces, but never appear as order targets.
  const orderAccounts = useMemo(() => (accounts ?? []).filter(isOrderEligibleAccount), [accounts])
  const accountExchange = useCallback(
    (account: PublicAccount) => toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue)),
    []
  )
  // The unique account shared by every SCOPED row (position/existing-order/
  // account-context-menu bound) on this exchange — an inherited row's accountId is
  // only ever a remembered/default guess, never real peer signal, so it's excluded
  // here. Split-account input (two differing scoped accounts) intentionally has no
  // peer rather than collapsing to an arbitrary one.
  const resolveScopedPeerAccountId = useCallback(
    (exchange: Exchange): string | undefined => {
      const scopedIds = new Set<string>()
      selectedInstruments.forEach((instrument) => {
        if (
          instrument.accountBinding?.kind === 'scoped' &&
          instrument.accountId &&
          parseExchangeField(instrument.exchange, primaryExchange) === exchange
        ) {
          scopedIds.add(instrument.accountId)
        }
      })
      selectedOptions.forEach((option) => {
        if (
          option.accountBinding?.kind === 'scoped' &&
          option.accountId &&
          parseExchangeField(
            option.exchange ?? (option.ticker ? String(option.ticker.exchange) : undefined),
            primaryExchange
          ) === exchange
        ) {
          scopedIds.add(option.accountId)
        }
      })
      return scopedIds.size === 1 ? scopedIds.values().next().value : undefined
    },
    [primaryExchange, selectedInstruments, selectedOptions]
  )

  // The account for an INHERITED row on `exchange` — form override → scoped peer →
  // remembered → card → venue default (resolveAccount's 'new' intent). Every
  // account-authoritative decision for a non-scoped row — item normalization AND
  // getSubmissionRows — goes through this ONE function so "what the AccountTab
  // shows" and "what actually submits" can't diverge. No self-heal/write-back needed
  // for a stale `orderAccountOverrides` entry: every tier is re-validated live on
  // each call, so an invalid override is simply skipped, never stored-and-corrected.
  const resolveInheritedAccountId = useCallback(
    (exchange: Exchange): string | undefined => {
      const tradeVenue = orderAccounts.find(
        (account) => accountExchange(account) === exchange
      )?.tradeVenue
      const resolution = resolveAccount({
        accounts: orderAccounts,
        purpose: 'trade',
        intent: 'new',
        tradeVenue,
        surfaceAccountId: orderAccountOverrides[exchange],
        peerAccountId: resolveScopedPeerAccountId(exchange),
        userDefaultAccountId:
          tradeVenue === undefined ? undefined : defaultAccountByTradeVenue[tradeVenue],
        rememberedAccountId:
          tradeVenue === undefined ? undefined : lastAccountByTradeVenue[tradeVenue],
        cardAccountId: exchange === primaryExchange ? cardTradingAccountId : undefined,
      })
      return resolution.status === 'resolved' ? resolution.accountId : undefined
    },
    [
      orderAccounts,
      accountExchange,
      orderAccountOverrides,
      resolveScopedPeerAccountId,
      defaultAccountByTradeVenue,
      lastAccountByTradeVenue,
      primaryExchange,
      cardTradingAccountId,
    ]
  )

  // Validates a SCOPED row's own bound account — read-only/wrong-venue is caught
  // against `accounts` (excludes only suspended/inactive, per useUserAccounts'
  // default `showDisabled: false`); a genuinely inactive account is simply absent
  // from that list, so it surfaces as 'account-not-found' rather than 'inactive'.
  // Either way `resolution.status` is 'unresolved' and this returns undefined — the
  // distinction only matters if a caller ever surfaces `reason` in the UI, which none
  // does yet, so a second `showDisabled: true` query (as Hedge's own scoped check
  // uses) isn't worth the extra subscription here.
  // A scoped row never falls back to another account (resolveAccount's 'scoped'
  // intent) — it validates whatever accountId the row CURRENTLY carries, nothing
  // more. The user can still move that accountId to a different one via the
  // AccountTab (setOrderAccountFilter retargets scoped rows too); this function
  // just refuses to substitute one on its own when the current one is invalid.
  const resolveScopedAccountId = useCallback(
    (accountId: string, exchange: Exchange): string | undefined => {
      const tradeVenue = exchangeToTradeVenue(exchange)
      if (tradeVenue === undefined) return undefined
      const resolution = resolveAccount({
        accounts: accounts ?? [],
        purpose: 'trade',
        intent: 'scoped',
        tradeVenue,
        scopedAccountId: accountId,
      })
      return resolution.status === 'resolved' ? resolution.accountId : undefined
    },
    [accounts]
  )

  const resolveItemAccountId = useCallback(
    (
      item: { accountId?: string; accountBinding?: AccountBinding },
      exchange: Exchange
    ): string | undefined => {
      if (item.accountBinding?.kind === 'scoped') {
        return item.accountId ? resolveScopedAccountId(item.accountId, exchange) : undefined
      }
      return resolveInheritedAccountId(exchange)
    },
    [resolveInheritedAccountId, resolveScopedAccountId]
  )

  // User-facing account-tab pick for `exchange` — a local override the user made,
  // never a computed default (see orderAccountOverrides' own comment above). It does
  // NOT write back to the card's watch-list tab; it only persists a venue-level
  // fallback for a later inherited row that arrives without one.
  //
  // Retargets EVERY matching row on this exchange, scoped or inherited alike — the
  // "scoped never falls back" rule (resolveItemAccountId/resolveScopedAccountId) is
  // only about the RESOLVER never silently substituting another account on its own;
  // it does not restrict what the user explicitly clicks. A position on account A
  // seeded a scoped row here, but the user is free to submit the resulting order
  // against account B by picking B in the AccountTab — same as any other row. The
  // row's `accountBinding` (still 'scoped') is untouched: it keeps blocking a future
  // AUTOMATIC default from moving this row again, it just no longer points at A.
  const setOrderAccountFilter = useCallback(
    (exchange: Exchange, accountId: string, currentAccountId?: string) => {
      setOrderAccountOverrides((current) => ({ ...current, [exchange]: accountId }))

      const matchesCurrent = (itemAccountId?: string) =>
        currentAccountId ? itemAccountId === currentAccountId : !itemAccountId

      setSelectedInstruments((current) =>
        current.map((item) =>
          parseExchangeField(item.exchange, primaryExchange) === exchange &&
          matchesCurrent(item.accountId)
            ? { ...item, accountId }
            : item
        )
      )
      setSelectedOptions((current) =>
        current.map((item) =>
          parseExchangeField(
            item.exchange ?? (item.ticker ? String(item.ticker.exchange) : undefined),
            primaryExchange
          ) === exchange && matchesCurrent(item.accountId)
            ? { ...item, accountId }
            : item
        )
      )

      const account = orderAccounts.find(
        (candidate) => candidate.accountId === accountId && accountExchange(candidate) === exchange
      )
      if (!account) return
      setLastAccountByTradeVenue((current) =>
        current[account.tradeVenue] === accountId
          ? current
          : { ...current, [account.tradeVenue]: accountId }
      )
    },
    [
      accountExchange,
      orderAccounts,
      primaryExchange,
      setLastAccountByTradeVenue,
      setSelectedInstruments,
      setSelectedOptions,
    ]
  )

  return {
    orderAccounts,
    accountExchange,
    resolveInheritedAccountId,
    resolveItemAccountId,
    setOrderAccountFilter,
  }
}
